import { defineStore } from 'pinia';
import { isCancel } from 'axios';
import * as Sentry from '@sentry/vue';
import Api from '@/modules/core/api/api.ts';
import { IgnorableApiException } from '@/modules/common/models/exception/ignorable-api-exception.ts';
import { AuthEndPoint } from '@/modules/common/enums/end-point.ts';
import { useSyncSystemStore } from '@/modules/core/stores/sync-system.store.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { Route } from '@/modules/core/enums/route.enum.ts';
import { AppUtil } from '@/modules/core/utils/app.util.ts';
import { hydratePersistedStore } from '@/modules/core/utils/guarded-persist-storage.util.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import router from '@/router.ts';
import AuthApi from '@/modules/core/api/auth.api.ts';
import { TOKEN_REFRESH_ERROR_CODE } from '@/modules/core/consts/token-refresh-error-code.const.ts';
import type { LoginFormData } from '@/modules/core/components/LoginPage.vue';
import type { Tokens } from '@/modules/common/adapters/tokens.type.ts';
import type { AuthState } from '@/modules/core/types/auth-store.type.ts';

const AUTH_REFRESH_LOCK = 'ads-auth-refresh';

let isEndingSession = false;

export function endSession(showNotification = false) {
  if (isEndingSession) {
    return;
  }

  isEndingSession = true;

  try {
    AppUtil.clear();

    if (showNotification) {
      notification.warning('core.common.error.unauthorized');
    }

    const currentRoute = router.currentRoute.value;
    const isOnLoginPage = currentRoute.name === Route.LoginPage;

    void router.replace({
      name: Route.LoginPage,
      query: isOnLoginPage ? undefined : { redirect: currentRoute.fullPath },
    });
  } finally {
    isEndingSession = false;
  }
}

export const useAuthStore = defineStore(StoreId.Auth, {
  state: (): AuthState => ({
    accessToken: null,
    refreshToken: null,
  }),
  getters: {
    isAuthorized: (state) => !!state.accessToken,
  },
  actions: {
    set(tokens: Tokens) {
      this.accessToken = tokens.accessToken;
      this.refreshToken = tokens.refreshToken;
    },
    handleUnauthorized(showNotification = false) {
      const previousAccessToken = this.accessToken;
      hydratePersistedStore(this);

      if (this.accessToken && this.accessToken !== previousAccessToken) {
        return;
      }

      endSession(showNotification);
    },
    async login(request: LoginFormData) {
      const response = await AuthApi.login(request);

      const syncSystemStore = useSyncSystemStore();
      syncSystemStore.setDBType(response.dbType);
      syncSystemStore.setStorage(response.storageUrl);

      this.set(response);
    },
    async refresh() {
      const refreshTokenBeforeLock = this.refreshToken;

      const run = async () => {
        hydratePersistedStore(this);

        if (refreshTokenBeforeLock && this.refreshToken && this.refreshToken !== refreshTokenBeforeLock) {
          return;
        }

        if (!this.refreshToken) {
          this.handleUnauthorized();
          return;
        }

        const usedRefreshToken = this.refreshToken;

        try {
          this.set(await AuthApi.refresh(usedRefreshToken));
        } catch (error) {
          if (isCancel(error)) {
            throw error;
          }

          hydratePersistedStore(this);
          if (this.refreshToken && this.refreshToken !== usedRefreshToken) {
            return;
          }

          if (ErrorUtil.isUnexpected(error)) {
            Sentry.captureException(error);
          } else if (ErrorUtil.isUnauthorized(error) || ErrorUtil.isBodyStatus(error, TOKEN_REFRESH_ERROR_CODE)) {
            this.handleUnauthorized(true);
          }
          throw new IgnorableApiException(error);
        }
      };

      await (navigator.locks?.request(AUTH_REFRESH_LOCK, run) ?? run());
    },
    logout() {
      Api.post(AuthEndPoint.Logout).catch((error: unknown) => {
        throw new IgnorableApiException(error);
      });

      endSession();
    },
  },
  persist: true,
});
